/**
 * Helpers for presenting the opaque `gmailSenderId` shown in the PMS Processing Tool's
 * "Unassigned Property Management Files" table.
 *
 * A `gmailSenderId` is a base64 blob that decodes to a fixed shape:
 *   `[F]<Sender Name>-[T]sync]<per-file noise>`
 * The only human-meaningful part is the sender name; the rest is per-file noise. These
 * helpers extract that name (with a safe fallback) and tally how often each sender recurs.
 */

const SENDER_LABEL_REGEX = /\[F\](.*?)-\[T\]/;

const decodeBase64 = (value) => {
  try {
    if (typeof atob === 'function') {
      return atob(value);
    }
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(value, 'base64').toString('binary');
    }
  } catch (err) {
    return null;
  }
  return null;
};

/**
 * Shorten a long opaque id to a `head…tail` stub so it fits in a table cell.
 */
export const truncateSenderId = (value, head = 8, tail = 6) => {
  if (!value || value.length <= head + tail + 1) {
    return value || '';
  }
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
};

/**
 * Decode a `gmailSenderId` into its human-readable sender name. Falls back to a truncated
 * stub of the raw value when the blob can't be decoded or doesn't match the expected shape.
 * Never throws.
 */
export const decodeGmailSenderLabel = (rawSenderId) => {
  if (!rawSenderId) {
    return '';
  }
  const decoded = decodeBase64(rawSenderId);
  if (decoded) {
    const match = decoded.match(SENDER_LABEL_REGEX);
    if (match && match[1].trim()) {
      return match[1].trim();
    }
  }
  return truncateSenderId(rawSenderId);
};

/**
 * Build a `{ [decodedSenderLabel]: occurrenceCount }` map for a list of files, so the table
 * can flag senders that appear more than once.
 */
export const buildSenderLabelCountMap = (items = []) => {
  const counts = {};
  items.forEach((item) => {
    const label = decodeGmailSenderLabel(item && item.gmailSenderId);
    if (label) {
      counts[label] = (counts[label] || 0) + 1;
    }
  });
  return counts;
};
